import { Addressable } from '@caredna-marlin/utils/addresses';
import type { OptimizerPreSkipReason, DriveTimeOptimizerSkipReason, GoogleRouteOptimizationSkipReason } from '@caredna-marlin/constraint-optimizer/types';
import { isEqual, xor } from 'lodash';
import { compact } from 'lodash/fp';
import { CommandIndexCombinedDataQuery, ScheduleVisitFromVisitRequestMutation, ShiftStatusEnum, CalendarBlockBlockTypeEnum, DriveTimeOptimizerQuery } from '~/graphql/__generated__/graphql';

const verbose = false;

// Used to omit __typename from our Local objects; this assists in using them by *shape* (eg, by their selected properties)
// rather than by *how they were queried*. Especially handy when merging into values received from other queries
// https://stackoverflow.com/questions/54487137/how-to-recursively-omit-key-from-type
type OmitDistributive<T, K extends PropertyKey> = T extends any ? (T extends object ? Id<OmitRecursively<T, K>> : T) : never;
type Id<T> = {} & { [P in keyof T] : T[P]} // Cosmetic use only makes the tooltips expad the type can be removed
export type OmitRecursively<T, K extends PropertyKey> = Omit<
    { [P in keyof T]: OmitDistributive<T[P], K> },
    K
>

// ---------- Types ----------
export type LocalVisitResource = OmitRecursively<CommandIndexCombinedDataQuery["visitResource"][number] & {
  visit: NonNullable<CommandIndexCombinedDataQuery["visitResource"][number]["visit"]>,
}, "__typename">
export type LocalVisitResourceWithVisit = LocalVisitResource & { visit: NonNullable<LocalVisitResource['visit']> }
// NOTE: typescript still complains about partial updates to visit
export type LocalVisitResourcePatch = Partial<LocalVisitResource>
export type LocalCalendarBlockPatch = Partial<LocalCalendarBlock>
export type LocalShift = OmitRecursively<CommandIndexCombinedDataQuery["shift"][number], "__typename">
export type LocalCalendarBlock = OmitRecursively<CommandIndexCombinedDataQuery["calendarBlock"][number] & { blockType: CalendarBlockBlockTypeEnum}, "__typename">

export type DriveTimePatch = {
  timestamp: number,
  shiftId: string,
  patch: Record<string, Partial<
    Pick<LocalVisitResource, 'startOfShiftDriveTime' | 'interVisitDriveTime' | 'endOfShiftDriveTime'>
  >>
}
export type LocalVisitResourceModifications = Record<string, LocalVisitResourcePatch>
export type LocalCalendarBlockModifications = Record<string, LocalCalendarBlockPatch>
export type NewLocalVisitResources = LocalVisitResource[]
export type NewLocalCalendarBlocks = LocalCalendarBlock[]
export type LocalVisitResourceBase = LocalVisitResource[]
export type LocalShiftBase = LocalShift[]
export type LocalCalendarBlockBase = LocalCalendarBlock[]
export type CurrentUserId = number | undefined
export type DriveUpdatesNeeded = Record<string, number>
export type SkippedVisitResource = LocalVisitResource & {
  skippedReason: string;
}

export type LocalVisit = NonNullable<LocalVisitResource['visit']> & {
  visitResources: Omit<LocalVisitResource, 'visit'>[];
};
export type CurrentDatabaseViewDateRange = {
  filterStartTime: string,
  filterEndTime: string
} | undefined

export type SetDbResourcesPayload = {
  resources: LocalVisitResource[],
  blocks: LocalCalendarBlock[],
  shifts: LocalShift[],
  dbDateRange: NonNullable<CurrentDatabaseViewDateRange>,
}

// Stores information we need to make a new visit, but don't query on existing visits
// This could also be stored in each visit, but would be duplicated for multi-resource
export type VisitMetaData = {
  taskId: number;
  serviceLineId: number;
  serviceIds: number[];
  address: Addressable;
  duration: number;
}
export type visitMetaDataLookup = Record<string, VisitMetaData> // key is visitID

export type VisitCreationResult = {
  oldVisitId: number,
  result: ScheduleVisitFromVisitRequestMutation["scheduleOverrideVisit"] | undefined
}

export type DriveTimeOptimizationResource = {
  patient: LocalVisit["patient"]
  originalVisit: Partial<LocalVisit>
  optimizedVisit: Partial<LocalVisit>
  originalProviderId: number | null
  optimizedVisitStartTime: string
  newProviderId: number | null
  originalDriveTime: number
  newDriveTime: number
}

// ---------- State ----------
export interface CommandCenterReducerState {
  modifiedVisitResources: LocalVisitResourceModifications;
  newVisitResources: NewLocalVisitResources;
  modifiedCalendarBlocks: LocalCalendarBlockModifications;
  dbVisitResources: LocalVisitResourceBase;
  dbShifts: LocalShiftBase;
  dbCalendarBlocks: LocalCalendarBlockBase;
  dbChangeDetected: boolean;
  driveUpdatesNeeded: DriveUpdatesNeeded;
  currentUser: CurrentUserId;
  dbDateRange: CurrentDatabaseViewDateRange;
  visitMetaDataLookup: visitMetaDataLookup;
  optimizerReviewData: {
    originalVisitResources: LocalVisitResource[];
    skippedVisitResources: SkippedVisitResource[];
    assignmentIds: number[];
  };
}

// ---------- Actions ----------
export type Action =
  | { type: 'MODIFY_RESOURCES'; payload: Record<string, LocalVisitResourcePatch> }
  | { type: 'MODIFY_CALENDAR_BLOCKS'; payload: Record<string, LocalCalendarBlockPatch> }
  | { type: 'ADD_RESOURCE'; payload: LocalVisitResource }
  | { type: 'REMOVE_VISIT'; payload: number }
  | { type: 'SET_DB_RESOURCES'; payload: SetDbResourcesPayload }
  | { type: 'COMPLETE_DRIVE_TIME'; payload: DriveTimePatch }
  | { type: 'SET_CURRENT_USER'; payload: CurrentUserId}
  | { type: 'RESET_DB_CHANGE_DETECTOR'}
  | { type: 'RESET_MODIFICATIONS' }
  | { type: 'STORE_VISIT_METADATA'; payload: {visitId: string, metaData: VisitMetaData}}
  | { type: 'VISIT_RESOURCE_UPDATE_FINISHED'; payload: number[] } // visitResource id
  | { type: 'VISIT_CREATE_FINISHED'; payload: VisitCreationResult}
  | { type: 'CALENDAR_BLOCK_UPDATE_FINISHED'; payload: number}
  | { type: 'SET_ORIGINAL_VISIT_RESOURCES'; payload: number[]}
  | { type: 'SET_SKIPPED_VISIT_RESOURCES'; payload: { skipped: DriveTimeOptimizerQuery["driveTimeOptimizer"]["skipped"]}}
  | { type: 'SET_OPTIMIZER_ASSIGNMENT_IDS'; payload: number[]};

// ---------- Sub-Reducers ----------

// Handles local modifications and removal of edits if visit is canceled in the DB
export function reduceModifiedVisitResources(
  modified: LocalVisitResourceModifications,
  action: Action,
  state: CommandCenterReducerState
): LocalVisitResourceModifications {
  switch (action.type) {
    case 'MODIFY_RESOURCES': {
      const result = { ...modified };
      for (const [id, patch] of Object.entries(action.payload)) {
        result[id] = { ...result[id], ...patch };
      }
      return result;
    }
    case 'COMPLETE_DRIVE_TIME': {
      const { patch, timestamp, shiftId } = action.payload;

      if (!shouldApplyPatch(timestamp, state.driveUpdatesNeeded[shiftId])) {
        return modified
      }
      const result = { ...modified };
      for (const [id, changes] of Object.entries(patch)) {
        result[id] = { ...result[id], ...changes };
      }

      return result;
    }
    case 'SET_DB_RESOURCES': {
      const { resources: newDb } = action.payload;
      let changed = false;
      const result = { ...modified };
      for (const res of newDb) {
        if (res.visit.eventStatus === 'canceled') {
          delete result[res.id.toString()];
          changed = true;
        }
      }
      return changed ? result : modified;
    }
    case 'ADD_RESOURCE': {
      // A new resource should count as "modified" and we use the keys of modifiedVisitResources to represent that
      const result = { ...modified };
      result[action.payload.id] = {}
      return result
    }
    case 'REMOVE_VISIT': {
      return removeModsByVisitId(modified, state.newVisitResources, action.payload);
    }
    case 'VISIT_CREATE_FINISHED': {
      return removeModsByVisitId(modified, state.newVisitResources, action.payload.oldVisitId);
    }
    case 'VISIT_RESOURCE_UPDATE_FINISHED': {
      const idToRemove = action.payload.toString();
      if (!(idToRemove in modified)) return modified;
      const result = { ...modified };
      delete result[idToRemove];
      return result;
    }
    case 'RESET_MODIFICATIONS': {
      return {};
    }
    default:
      return modified;
  }
}

export function reduceModifiedCalendarBlocks(
  modified: LocalCalendarBlockModifications,
  action: Action,
  state: CommandCenterReducerState
): LocalCalendarBlockModifications {
  switch (action.type) {
    case 'MODIFY_CALENDAR_BLOCKS': {
      const result = { ...modified };
      for (const [id, patch] of Object.entries(action.payload)) {
        result[id] = { ...result[id], ...patch };
      }
      return result;
    }

    case 'COMPLETE_DRIVE_TIME': {
      const { patch, timestamp, shiftId } = action.payload;

      if (!shouldApplyPatch(timestamp, state.driveUpdatesNeeded[shiftId])) {
        return modified
      }


      const result = { ...modified };
      for (const [key, changes] of Object.entries(patch)) {
        const id = key.match(/calendarBlock-(\d+)/)?.[1];
        if (id) {
          result[id] = { ...(result[id] || {}), ...changes };
        }
      }

      return result;
    }

    case 'SET_DB_RESOURCES': {
      const { resources: newDb } = action.payload;
      let changed = false;
      const result = { ...modified };
      for (const res of newDb) {
        if (res.visit.eventStatus === 'canceled') {
          delete result[res.id.toString()];
          changed = true;
        }
      }
      return changed ? result : modified;
    }

    case 'CALENDAR_BLOCK_UPDATE_FINISHED': {
      const idToRemove = action.payload.toString();
      if (!(idToRemove in modified)) return modified;
      const result = { ...modified };
      delete result[idToRemove];
      return result;
    }

    case 'RESET_MODIFICATIONS': {
      return {};
    }

    default: {
      return modified
    }
  }
}

// Handles new visitResources not in DB
// Just for initial creation, any modifications will be in modifiedVisitResources
export function reduceNewVisitResources(
  newResources: NewLocalVisitResources,
  action: Action
): NewLocalVisitResources {
  switch (action.type) {
    case 'ADD_RESOURCE': {
      return newResources.concat(action.payload)
    }
    case 'RESET_MODIFICATIONS': {
      return [];
    }
    case 'REMOVE_VISIT': {
      return newResources.filter((r) => r.visitId != action.payload)
    }
    case 'VISIT_CREATE_FINISHED': {
      return newResources.filter((r) => r.visitId != action.payload.oldVisitId)
    }
    default: {
      return newResources
    }
  }
}

// Replaces previous DB snapshot
export function reduceDbVisitResources(
  prevDb: LocalVisitResourceBase,
  action: Action,
  state: CommandCenterReducerState,
): LocalVisitResourceBase {
  switch (action.type) {
    case 'SET_DB_RESOURCES': {
      return action.payload.resources;
    }
    case 'VISIT_RESOURCE_UPDATE_FINISHED': {
      return prevDb.map((r) =>
        (action.payload.includes(r.id) && state.modifiedVisitResources[r.id]) ? { ...r, ...state.modifiedVisitResources[r.id] } : r
      );
    }
    case 'VISIT_CREATE_FINISHED': {
      // Take the non-persisted newVisitResources and replace with real ids and add to dbVisitResources
      // These will all be replaced by the next poll
      const { oldVisitId, result } = action.payload;
      if (!result) return prevDb;

      const newVisitId = result.id;
    
      const newResources = state.newVisitResources.filter((r) => r.visitId === oldVisitId);
      if (newResources.length === 0) return prevDb;
    
      const patchedResources = newResources.map((vr) => {
        const patch = state.modifiedVisitResources[vr.id.toString()] ?? {};
        const patchedLocal = {...vr, ...patch}

        const matchedDbResource = result.visitResources.find(
          (res) => res.providerId === patchedLocal.providerId
        );
        
        if (!matchedDbResource) return patchedLocal; // fallback if no match found
    
        return {
          ...patchedLocal,
          id: matchedDbResource.id,
          visitId: newVisitId,
          visit: {
            ...patchedLocal.visit,
            id: newVisitId,
          },
          startOfShiftDriveTime: matchedDbResource.startOfShiftDriveTime,
          interVisitDriveTime: matchedDbResource.interVisitDriveTime,
          endOfShiftDriveTime: matchedDbResource.endOfShiftDriveTime,
        };
      });
    
      return [...prevDb, ...patchedResources];
    }
    default:
        return prevDb;
  }
}

// Replaces previous DB shifts snapshot
function reduceDbShifts(
  prevDbShifts: LocalShiftBase,
  action: Action
): LocalShiftBase {
  if (action.type === 'SET_DB_RESOURCES') {
    return action.payload.shifts;
  }
  return prevDbShifts;
}

// Replaces previous DB calendar blocks snapshot
function reduceDbCalendarBlocks(
  prevDbCalendarBlocks: LocalCalendarBlockBase,
  action: Action,
  state: CommandCenterReducerState
): LocalCalendarBlockBase {
  if (action.type === 'SET_DB_RESOURCES') {
    return action.payload.blocks;
  } else if (action.type === 'CALENDAR_BLOCK_UPDATE_FINISHED') {
    const patch = state.modifiedCalendarBlocks[action.payload.toString()];
    if (!patch) return prevDbCalendarBlocks;

    return prevDbCalendarBlocks.map((cb) =>
      cb.id === action.payload ? { ...cb, ...patch } : cb
    );
  }
  return prevDbCalendarBlocks;
}

// Monitors for external DB changes from other users
// Can be reset after user is notified
export function reduceDbChangeDetector(
  dbChangeDetected: boolean,
  action: Action,
  state: CommandCenterReducerState
): boolean {
  switch (action.type) {
    case 'SET_DB_RESOURCES': {
      const { resources, shifts, blocks } = action.payload;

      return (
        getChangedItemsIds(resources, state, action.payload.dbDateRange, true, 'visitResource').length > 0
        || getChangedItemsIds(shifts, state, action.payload.dbDateRange, true, 'shift').length > 0
        || getChangedItemsIds(blocks, state, action.payload.dbDateRange, true, 'calendarBlock').length > 0
        || dbChangeDetected
      );
    }
    case 'RESET_DB_CHANGE_DETECTOR': {
      return false
    }
    default: return dbChangeDetected;
  }
}

// Manages the driveUpdatesNeeded list
export function reduceDriveUpdatesNeeded(
  drivePendingIds: DriveUpdatesNeeded,
  action: Action,
  state: CommandCenterReducerState
): DriveUpdatesNeeded {
  const now = (new Date()).getTime()
  switch (action.type) {
    case 'ADD_RESOURCE': {
      const shift = getShiftForResource(action.payload, state)
      if (!shift) return drivePendingIds;
      return {...drivePendingIds, ...{[shift.id.toString()]: now}}
    }
    case 'REMOVE_VISIT': {
      const matchingResources = state.newVisitResources.filter(
        (r) => r.visitId?.toString() === action.payload?.toString()
      );
      if (matchingResources.length === 0) return drivePendingIds;
    
      return matchingResources.reduce((acc, resource) => {
        const shift = getShiftForResource(resource, state);
        if (!shift) return acc;
        acc[shift.id.toString()] = now;
        return acc;
      }, { ...drivePendingIds });
    }
    case 'MODIFY_RESOURCES': {
      const newPending = Object.entries(action.payload).flatMap(([id, patch]) => {
        const currentResource = currentVisitResourceById(id, state)
        if (!currentResource) return [] // NOTE: All modifications should have a base object
        return compact([
          // The shift the resource was before this action
          getShiftForResource(currentResource, state),
          // The shift the resource will be on after this action
          getShiftForResource({...currentResource, ...patch}, state)
        ])
      })
      if (newPending.length == 0) return drivePendingIds;

      return {...drivePendingIds, ...createTimestampMap(newPending, now)}
    }
    case 'MODIFY_CALENDAR_BLOCKS': {
      const newPending = Object.entries(action.payload).flatMap(([id, patch]) => {
        const currentCalendarBlock = currentCalendarBlockById(id, state)
        if (!currentCalendarBlock) return []
        return compact([
          getShiftForCalendarBlock(currentCalendarBlock, state),
          getShiftForCalendarBlock({...currentCalendarBlock, ...patch}, state)
        ])
      })
      if (newPending.length == 0) return drivePendingIds;

      return {...drivePendingIds, ...createTimestampMap(newPending, now)}
    }
    case 'SET_DB_RESOURCES': {
      // on DB sync, detect changed resources
      const { resources: newDb, dbDateRange: prevDbDateRange } = action.payload;
      const { modifiedVisitResources: modified, newVisitResources: newVR } = state;

      // skip when switching dates
      if (!areDbDateRangesEqual(prevDbDateRange, state.dbDateRange)) {
        return state.driveUpdatesNeeded;
      }

      // If there are no shifts in memory - initial load - don't fetch drive times for every shift loaded from the db
      const changedShifts = state.dbShifts.length ? xor(action.payload.shifts, state.dbShifts) : [];

      // If there are no local modifications and no updated shifts, trust the drive times from the DB
      if (Object.keys(modified).length === 0 && newVR.length === 0 && changedShifts.length === 0) return drivePendingIds;

      const newPending = getChangedItemsIds(newDb, state, action.payload.dbDateRange, false, 'visitResource').flatMap((changedId) => {
        const currentResource = currentVisitResourceById(changedId, state)
        const futureResourceBase = newDb.find((r) => r.id.toString() == changedId)
        const futureResource = {...futureResourceBase, ...modified[changedId] as LocalVisitResource}
        return compact([
          // The shift the db resource was before this action
          currentResource ? getShiftForResource(currentResource, state) : null,
          // The shift the db resource will be on after this action
          futureResource ? getShiftForResource(futureResource, state) : null
        ])
      })
      if (newPending.length === 0 && changedShifts.length === 0) return drivePendingIds;

      return {
        ...drivePendingIds,
        ...createTimestampMap(newPending, now),
        ...createTimestampMap(changedShifts, now),
      }
    }
    case 'COMPLETE_DRIVE_TIME': {
      const { shiftId, timestamp } = action.payload;
      let removedSomething = false;
      const updated: DriveUpdatesNeeded = {};
    
      for (const [id, ts] of Object.entries(drivePendingIds)) {
        if (id != shiftId || !shouldApplyPatch(timestamp, ts)) {
          // patch does not match drivePending id, so keep it pending
          updated[id] = ts;
        } else {
          // patch matches drivePending id, so don't keep it pending
          removedSomething = true;
        }
      }
    
      return removedSomething ? updated : drivePendingIds;
    }
    case 'RESET_MODIFICATIONS': {
      return {}
    }
    default:
      return drivePendingIds;
  }
}

// Manages the current user
// Only called once, and not needed until the first poll
// so doesn't matter if it's not set for initial DB dump.
export function reduceCurrentUser(
  currentUser: CurrentUserId,
  action: Action
): CurrentUserId {
  switch (action.type) {
    case 'SET_CURRENT_USER':
      return action.payload
    default:
      return currentUser
  }
}

// Monitors what date range our dbVisitResources cover
export function reduceDbDateRange(
  dbDateRange: CurrentDatabaseViewDateRange,
  action: Action
): CurrentDatabaseViewDateRange {
  switch (action.type) {
    case 'SET_DB_RESOURCES': {
      if (areDbDateRangesEqual(action.payload.dbDateRange, dbDateRange)) return dbDateRange;
      return { ...action.payload.dbDateRange };
    }
    default: return dbDateRange;
  }
}

// Update the visit metadata mapping
export function reduceVisitMetaDataLookup(
  metaDataLookup: visitMetaDataLookup,
  action: Action
): visitMetaDataLookup {
  switch (action.type) {
    case 'STORE_VISIT_METADATA': {
      const lookup = {...metaDataLookup}
      lookup[action.payload.visitId] = action.payload.metaData;
      return lookup;
    }
    case 'REMOVE_VISIT': {
      // If a visit is removed, remove the metadata so the visitRequest shows back up
      const visitIdToRemove = action.payload.toString();
      if (!(visitIdToRemove in metaDataLookup)) return metaDataLookup;
      const result = { ...metaDataLookup };
      delete result[visitIdToRemove];
      return result;
    }    
    case 'RESET_MODIFICATIONS': {
      return {}
    }
    default: return metaDataLookup;
  }
}


const googleRouteOptimizationSkipReasonCopy: Record<GoogleRouteOptimizationSkipReason, string> = {
  CODE_UNSPECIFIED:
    "Could not be optimized for an unknown reason",

  NO_VEHICLE:
    "No providers are available to take this visit",

  MULTI_RESOURCE_SIBLING_SKIPPED:
    "Another component of this multi-resource visit could not be scheduled",

  DEMAND_EXCEEDS_VEHICLE_CAPACITY:
    "No provider can take this visit due to blocked time (meetings, facility visits, visit type filter blocks)",

  CANNOT_BE_PERFORMED_WITHIN_VEHICLE_DISTANCE_LIMIT:
    "No providers can take this visit without exceeding travel distance limits",

  CANNOT_BE_PERFORMED_WITHIN_VEHICLE_DURATION_LIMIT:
    "No providers can take this visit within their shift duration limits",

  CANNOT_BE_PERFORMED_WITHIN_VEHICLE_TRAVEL_DURATION_LIMIT:
    "No providers can take this visit within allowed travel time limits",

  CANNOT_BE_PERFORMED_WITHIN_VEHICLE_TIME_WINDOWS:
    "No providers can take this visit within the required time window",

  VEHICLE_NOT_ALLOWED:
    "No eligible providers are allowed to perform this visit",
};

const optimizerPreSkipReasonCopy: Record<OptimizerPreSkipReason, string> = {
  MISCONFIGURED_PROVIDER_REQUIREMENTS:
    "Misconfigured provider requirements for visit type",
  PROVIDER_TYPE:
    "No providers found matching any of the required provider types",
  SERVICE_LINE:
    "No otherwise allowed providers are assigned to the current service line",
  STATE_LICENSE:
    "No otherwise allowed providers match state license requirements",
  NO_OVERLAPPING_SHIFTS:
    "No overlapping shifts for visit arrival window",
  NO_LOCKED_PROVIDER_SHIFTS:
    "No shifts for locked provider",
  NO_ACCEPTABLE_PROVIDER_SHIFTS:
    "No shifts for acceptableProviderIds",
  NO_TRAVEL_TYPE_SHIFTS:
    "No shifts matching travel type",
  NO_OVERFLOW_FILTER_SHIFTS:
    "No shifts matching overflow filter",
  NO_MAX_IN_HOME_DISTANCE_SHIFTS:
    "No shifts within max in home distance",
  NO_NON_ERRAND_ANCHOR:
    "No non-errand visit resource to schedule this errand against",
};

const optimizerSkipReasonCopy: Record<DriveTimeOptimizerSkipReason, string> = {
  ...googleRouteOptimizationSkipReasonCopy,
  ...optimizerPreSkipReasonCopy,
};

const formatOptimizerRoutingReason = (reasons?: string[]) => {
  if (!reasons?.length) return `${optimizerSkipReasonCopy.CODE_UNSPECIFIED}.`;
  return `${reasons.map((r) => optimizerSkipReasonCopy[r as DriveTimeOptimizerSkipReason] ?? r).filter(Boolean).join(', ')}.`;
}

// saves state for skipped visits when clicking optimize button
export function reduceOptimizerReviewData(
  current: CommandCenterReducerState["optimizerReviewData"],
  action: Action,
  state: CommandCenterReducerState
): CommandCenterReducerState["optimizerReviewData"] {
  switch (action.type) {
    case 'SET_ORIGINAL_VISIT_RESOURCES': {
      const optimizedVisitResourcesIds = action.payload;

      if (!optimizedVisitResourcesIds.length) return current;

      const localVisitResources = combineResources(state);
      const existingIds = new Set(current.originalVisitResources.map((vr) => vr.id));

      const newSnapshots: LocalVisitResource[] = [];

      for (const id of optimizedVisitResourcesIds) {
        if (existingIds.has(id)) continue;
        const found = localVisitResources.find((vr) => vr.id === id);
        if (found) newSnapshots.push(found);
      }

      if (!newSnapshots.length) return current;

      return {
        ...current,
        originalVisitResources: [...current.originalVisitResources, ...newSnapshots],
      };
    }
    case 'SET_SKIPPED_VISIT_RESOURCES': {
      const skippedVisitResourceIds = action.payload.skipped.map((s) => s.visitResourceId);

      if (!skippedVisitResourceIds.length) return current;

      const localVisitResources = combineResources(state);

      const skippedVisitResources = localVisitResources
        .filter((vr) => skippedVisitResourceIds.includes(vr.id))
        .map((vr) => {
          const skippedData = action.payload.skipped.find((s) => s.visitResourceId === vr.id);
          const formattedReason = formatOptimizerRoutingReason(skippedData?.reasons);
          return {
            skippedReason: formattedReason,
            ...vr
          };
        });

      return {
        ...current,
        skippedVisitResources,
      };
    }
    case 'SET_OPTIMIZER_ASSIGNMENT_IDS': {
      return {
        ...current,
        assignmentIds: action.payload,
      };
    }
    case 'MODIFY_RESOURCES': {
      const updatedIds = new Set(Object.keys(action.payload).map((id) => parseInt(id, 10)));
      if (!updatedIds.size) return current;
      const remainingSkipped = current.skippedVisitResources.length
        ? current.skippedVisitResources.filter((vr) => !updatedIds.has(vr.id))
        : current.skippedVisitResources;
      const assignmentIds = new Set(current.assignmentIds);
      updatedIds.forEach((id) => assignmentIds.add(id));

      const updatedAssignmentIds = Array.from(assignmentIds);
      if (
        remainingSkipped.length === current.skippedVisitResources.length &&
        updatedAssignmentIds.length === current.assignmentIds.length
      ) {
        return current;
      }

      return {
        ...current,
        skippedVisitResources: remainingSkipped,
        assignmentIds: updatedAssignmentIds,
      };
    }
    case 'RESET_MODIFICATIONS': {
      return {
        originalVisitResources: [],
        skippedVisitResources: [],
        assignmentIds: [],
      };
    }
    default:
      return current;
  }
}

// ------------ Helpers -------------

/**
 * Merge together:
 *  1) dbResources (may be undefined)
 *  2) newResources (full, non-partial LocalVisitResources)
 *  3) modifiedResources (partials to apply on the base set)
 *
 * If the same ID appears in dbResources and newResources, newResources wins.
 * Any partial in modifiedResources will override the merged base.
 */
export function combineResources({
  dbVisitResources,
  newVisitResources,
  modifiedVisitResources,
}: CommandCenterReducerState): LocalVisitResource[] {
  const result: Record<string, LocalVisitResource> = {}

  // 1) seed from DB
  for (const db of dbVisitResources) {
    result[db.id.toString()] = db
  }

  // 2) add newly-created items
  for (const nr of newVisitResources) {
    result[nr.id.toString()] = nr
  }

  // 3) apply patches
  for (const [key, patch] of Object.entries(modifiedVisitResources)) {
    const base = result[key]
    if (!base) continue

    // start with a shallow merge
    const merged = { ...base, ...patch }

    // only merge the nested `visit` if base.visit actually exists
    if (base.visit) {
      merged.visit = { ...base.visit, ...(patch.visit ?? {}) }
    }

    result[key] = merged
  }

  return Object.values(result)
}

/**
 * Merge together:
 *  1) dbCalendarBlocks
 *  2) modifiedCalendarBlocks (partials to apply on the base set)
 *
 * Any partial in modifiedCalendarBlocks will override the merged base.
 */
export function combineCalendarBlocks({
  dbCalendarBlocks,
  modifiedCalendarBlocks,
}: CommandCenterReducerState): LocalCalendarBlock[] {
  const result: Record<string, LocalCalendarBlock> = {}

  for (const db of dbCalendarBlocks) {
    result[`calendarBlock-${db.id}`] = db
  }

  for (const [key, patch] of Object.entries(modifiedCalendarBlocks)) {
    const base = result[`calendarBlock-${key}`];
    if (!base) continue

    result[`calendarBlock-${key}`] = { ...base, ...patch }
  }

  return Object.values(result)
}

/**
 * Returns the set of IDs that either
 *  - exist in localVisitResources but not in dbVisitResources, or
 *  - whose full object differs (deeply) from the db version.
 */
export function findModifiedResourceIds(
  localVisitResources: LocalVisitResource[],
  dbVisitResources: LocalVisitResource[]
): Set<number> {
  const modified = new Set<number>()
  // build a quick map of the DB rows
  const dbById = new Map<number, LocalVisitResource>()
  for (const db of dbVisitResources) {
    dbById.set(db.id, db)
  }

  // for each local entry, check “missing or not equal”
  for (const local of localVisitResources) {
    const db = dbById.get(local.id)
    // either brand-new, or deep-unequal
    if (!db || !isEqual(local, db)) {
      modified.add(local.id)
    }
  }

  return modified
}

export const getChangedItemsIds = (
  newDb: LocalVisitResource[] | LocalCalendarBlock[] | LocalShift[],
  state: CommandCenterReducerState,
  dbDateRange: CurrentDatabaseViewDateRange,
  filterCurrentUser: boolean,
  itemType: 'visitResource' | 'shift' | 'calendarBlock'
): string[] => {
  const { dbVisitResources, dbShifts, dbCalendarBlocks, dbDateRange: prevDbDateRange, currentUser } = state;

  if (dbDateRange !== undefined) {
    // Ignore the initial load, and ignore changes when switching days
    if (prevDbDateRange === undefined || !areDbDateRangesEqual(prevDbDateRange, dbDateRange)) { return [] }
  }
  
  return compact(newDb.map((next) => {
    // we have to make sure that either we don't care who updated it (e.g., deciding to recalculate drive times)
    // or that the change was done by someone else (e.g., deciding to notify the user that someone else changed things)
    if (filterCurrentUser && next.updatedBy == currentUser) return '';

    let changed = false;

    if (itemType === 'visitResource') {
      const prev = dbVisitResources.find(r => r.id === next.id);
      if (!prev) return next.id.toString(); // new resource from DB
      changed = hasResourceChanged(prev, next as LocalVisitResource);
    } else if (itemType === 'shift') {
      const prevShift = dbShifts.find(s => s.id === next.id);
      if (!prevShift) return next.id.toString(); // new shift from DB
      changed = hasShiftChanged(prevShift, next as LocalShift);
    } else if (itemType === 'calendarBlock') {
      const prevCalendarBlock = dbCalendarBlocks.find(cb => cb.id === next.id);
      if (!prevCalendarBlock) return next.id.toString(); // new calendar block from DB
      changed = hasCalendarBlockChanged(prevCalendarBlock, next as LocalCalendarBlock);
    }

    return changed ? next.id.toString() : ''
  }))
}

const hasCalendarBlockChanged = (prev: LocalCalendarBlock, next: LocalCalendarBlock): boolean => {
  return (
      next.startTime !== prev.startTime ||
      next.endTime !== prev.endTime ||
      next.providerId !== prev.providerId ||
      next.title !== prev.title ||
      next.eventStatus !== prev.eventStatus
  );
}

const hasShiftChanged = (prev: LocalShift, next: LocalShift): boolean => {
  return (
    next.startTime !== prev.startTime ||
    next.endTime !== prev.endTime ||
    next.providerId !== prev.providerId ||
    next.status !== prev.status
  );
}

function hasResourceChanged(prev: LocalVisitResource, next: LocalVisitResource): boolean {
  return (
      next.startTime !== prev.startTime ||
      next.endTime !== prev.endTime ||
      next.providerId !== prev.providerId
  )
  // NOTE: Items should never be deleted by another user, only marked as cancelled.
}

function areDbDateRangesEqual(
  a: CurrentDatabaseViewDateRange,
  b: CurrentDatabaseViewDateRange
): boolean {
  if (a === b) return true;
  if (!a || !b) return false;
  return a.filterStartTime === b.filterStartTime && a.filterEndTime === b.filterEndTime;
}

function getShiftForResource(resource: LocalVisitResource, state: CommandCenterReducerState): LocalShift | undefined {
  return state.dbShifts.find((s) => {
    return resource.startTime >= s.startTime &&
      resource.endTime <= s.endTime &&
      s.provider?.id == resource.providerId &&
      s.status == ShiftStatusEnum.Confirmed
  })
}

function currentVisitResourceById(resourceId: string, state: CommandCenterReducerState): LocalVisitResource | undefined {
  const dbAndNew = (state.dbVisitResources || []).concat(state.newVisitResources) 
  const currentResourceBase = dbAndNew.find((cur) => cur.id.toString() == resourceId)
  if (currentResourceBase) {
    return {...currentResourceBase, ...state.modifiedVisitResources[resourceId]}
  } else {
    return undefined
  }
}

function currentCalendarBlockById(calendarBlockId: string, state: CommandCenterReducerState): LocalCalendarBlock | undefined {
  const currentCalendarBlockBase = state.dbCalendarBlocks.find((cb) => cb.id.toString() == calendarBlockId)
  if (currentCalendarBlockBase) {
    return {...currentCalendarBlockBase, ...state.modifiedCalendarBlocks[calendarBlockId]}
  } else {
    return undefined
  }
}

function getShiftForCalendarBlock(calendarBlock: LocalCalendarBlock, state: CommandCenterReducerState): LocalShift | undefined {
  return state.dbShifts.find((s) => {
    return calendarBlock.startTime >= s.startTime &&
      calendarBlock.endTime <= s.endTime &&
      s.provider?.id == calendarBlock.providerId &&
      s.status == ShiftStatusEnum.Confirmed
  })
}

// TODO: return same object if no effect?
function removeModsByVisitId(
  modified: LocalVisitResourceModifications,
  newVisitResources: NewLocalVisitResources,
  visitIdToRemove: number
): LocalVisitResourceModifications {
  return Object.fromEntries(
    Object.entries(modified).filter(([key]) => {
      const resource = newVisitResources.find((r) => r.id.toString() === key);
      // Remove any modification that matches a newVisitResource that's being removed
      return !resource || resource.visitId.toString() !== visitIdToRemove.toString();
    })
  );
}

function createTimestampMap(shifts: LocalShift[], timestamp: number): Record<string, number> {
  return Object.fromEntries(shifts.map(shift => [shift.id, timestamp]));
}

function shouldApplyPatch(
  patchTimestamp: number,
  pendingTimestamp: number | undefined
): boolean {
  return pendingTimestamp !== undefined && patchTimestamp >= pendingTimestamp;
}

export const calculateDriveTimeOptimizations = (originalResources: LocalVisitResource[], optimizedResources: LocalVisitResource[]): DriveTimeOptimizationResource[] => {
  const result = originalResources?.map((original) => {
    const optimized = optimizedResources.find((r) => r.id === original.id);
    if (!optimized) return null;
    const newProviderId = original?.providerId !== optimized?.providerId ? optimized?.providerId : null;

    const originalDriveTime = original.startOfShiftDriveTime + original.interVisitDriveTime;
    const newDriveTime = optimized.startOfShiftDriveTime + optimized.interVisitDriveTime;
    return {
      patient: original.visit.patient,
      originalProviderId: original?.providerId,
      originalVisit: original.visit,
      optimizedVisit: optimized.visit,
      optimizedVisitStartTime: optimized.startTime,
      newProviderId,
      originalDriveTime,
      newDriveTime,
    }
  });
  
  return compact(result);
}

// ---------- Root Reducer ----------
export function rootReducer(
  state: CommandCenterReducerState,
  action: Action
): CommandCenterReducerState {

  const newState = {
    modifiedVisitResources: reduceModifiedVisitResources(state.modifiedVisitResources, action, state),
    newVisitResources: reduceNewVisitResources(state.newVisitResources, action),
    modifiedCalendarBlocks: reduceModifiedCalendarBlocks(state.modifiedCalendarBlocks, action, state),
    dbVisitResources: reduceDbVisitResources(state.dbVisitResources, action, state),
    dbCalendarBlocks: reduceDbCalendarBlocks(state.dbCalendarBlocks, action, state),
    dbShifts: reduceDbShifts(state.dbShifts, action),
    dbChangeDetected: reduceDbChangeDetector(state.dbChangeDetected, action, state),
    driveUpdatesNeeded: reduceDriveUpdatesNeeded(state.driveUpdatesNeeded, action, state),
    currentUser: reduceCurrentUser(state.currentUser, action),
    dbDateRange: reduceDbDateRange(state.dbDateRange, action),
    visitMetaDataLookup: reduceVisitMetaDataLookup(state.visitMetaDataLookup, action),
    optimizerReviewData: reduceOptimizerReviewData(state.optimizerReviewData, action, state),
  };
  if (verbose) console.log("ACTION:", action, "\nNEW STATE:", newState)
  return newState;
}
